/**
 * Sequence Question Module
 * Handles rendering and validation of sequence/ordering questions
 * Extracted from main.js as part of Phase 2 modularization - Step 7
 */

import { EVENT_TYPES } from '../../core/event-types.js';
import { logEvent } from '../../core/event-logger.js';
import appState from '../../core/app-state.js';
import { i18nService } from '../../services/i18n-service.js';
import * as TranscriptModule from '../player/transcript.js';
import { generateOptionLabel } from './question-utils.js';
import { updateQuestionProgressBar, renderSummaryChip } from './question-manager.js';
import { renderCurrentQuestion } from './question-coordinator.js';
import { switchMode } from '../../core/router.js';
import { markClipCompleted, updateProgressUI, isClipCompleted, setCurrentClip } from '../../services/clip-coordinator.js';
import type { Clip, SequenceQuestion } from '../../types/clip.js';
import type { SequenceState } from '../../types/app-state.js';

/**
 * Render a sequence question where user must order items correctly
 * @param clip - The current clip
 * @param _idx - The clip index (unused but kept for interface consistency)
 */
export function renderSequenceQuestion(clip: Clip, _idx: number): void {
  const q = clip.questions[appState.currentQuestionIndex] as SequenceQuestion;
  const questionContainer = document.getElementById('questionContainer');
  if (!questionContainer) return;
  
  // Initialize sequence state if not already present
  if (!appState.sequenceState) {
    appState.sequenceState = {
      subStepIndex: 0,
      chosenIndices: [],
      correctOrder: q.correctOrder || []
    };
  }
  
  const sequenceState: SequenceState = appState.sequenceState;
  const items: string[] = q.items || [];
  
  // Clear the question container
  questionContainer.innerHTML = '';
  
  const questionDiv = document.createElement('div');
  questionDiv.className = 'question-block mt-1';
  questionDiv.setAttribute('role', 'group');
  questionDiv.setAttribute('aria-labelledby', `question-prompt-${clip.id}-${appState.currentQuestionIndex}`);
  
  // Clear the central feedback region (kept for backwards compatibility)
  const feedbackRegion = document.getElementById('feedback-region');
  if (feedbackRegion) {
    feedbackRegion.innerHTML = '';
  }
  
  // Add question prompt
  const questionText = document.createElement('p');
  questionText.textContent = q.prompt || i18nService.getString('reorderSteps') || 'Put the events in the correct order:';
  questionText.className = 'mb-3 text-lg font-semibold text-gray-800 text-center';
  questionText.id = `question-prompt-${clip.id}-${appState.currentQuestionIndex}`;
  questionDiv.appendChild(questionText);
  
  // Add progress indicator for sequence
  if (sequenceState.subStepIndex > 0) {
    const progressText = document.createElement('p');
    progressText.textContent = i18nService.formatUIString('nextStep', { current: sequenceState.subStepIndex + 1, total: items.length });
    progressText.className = 'text-sm text-gray-600 text-center mb-2';
    progressText.setAttribute('aria-live', 'polite');
    questionDiv.appendChild(progressText);
  }
  
  // Create buttons for each item
  const buttons: HTMLButtonElement[] = [];
  items.forEach((item, itemIndex) => {
    const btn = document.createElement('button');
    btn.className = 'answer-card';
    btn.setAttribute('role', 'option');
    btn.setAttribute('aria-selected', 'false');
    
    // Check if this item has already been chosen
    const alreadyChosen = sequenceState.chosenIndices.includes(itemIndex);
    
    if (alreadyChosen) {
      btn.disabled = true;
      btn.classList.add('locked', 'dim-unselected');
      btn.setAttribute('aria-disabled', 'true');
    }
    
    // Create badge and text
    const badge = document.createElement('span');
    badge.className = 'answer-badge';
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = generateOptionLabel(itemIndex);
    
    const srLabel = document.createElement('span');
    srLabel.className = 'sr-only';
    srLabel.textContent = `${generateOptionLabel(itemIndex)}. `;
    
    const textSpan = document.createElement('span');
    textSpan.className = 'answer-text';
    textSpan.textContent = item;
    
    btn.appendChild(badge);
    btn.appendChild(srLabel);
    btn.appendChild(textSpan);
    
    // Add click handler if not already chosen
    if (!alreadyChosen) {
      btn.addEventListener('click', () => {
        handleSequenceSelection(clip, q, itemIndex, buttons, questionDiv, sequenceState);
      });
    }
    
    buttons.push(btn);
    questionDiv.appendChild(btn);
  });
  
  // Create feedback container
  const questionFeedback = document.createElement('div');
  questionFeedback.className = 'question-feedback';
  questionFeedback.setAttribute('aria-live', 'polite');
  questionDiv.appendChild(questionFeedback);
  
  questionContainer.appendChild(questionDiv);
  
  // Log sequence question render
  logEvent(EVENT_TYPES['QUESTION_RENDER']!, {
    clipId: clip.id,
    questionIndex: appState.currentQuestionIndex,
    questionText: q.prompt,
    questionType: 'sequence',
    itemsCount: items.length,
    subStepIndex: sequenceState.subStepIndex
  });
}

/**
 * Handle sequence item selection
 * @param clip - The current clip
 * @param question - The question object
 * @param selectedItemIndex - Index of selected item
 * @param buttons - Array of button elements
 * @param questionDiv - The question container
 * @param sequenceState - The sequence state object
 */
function handleSequenceSelection(
  clip: Clip, 
  question: SequenceQuestion, 
  selectedItemIndex: number, 
  buttons: HTMLButtonElement[], 
  questionDiv: HTMLElement, 
  sequenceState: SequenceState
): void {
  // Check if buttons are already locked (shouldn't happen, but defensive)
  const btn = buttons[selectedItemIndex];
  if (!btn || btn.disabled) {
    return;
  }
  
  const correctOrder = sequenceState.correctOrder;
  const expectedIndex = correctOrder[sequenceState.subStepIndex];
  const isCorrect = selectedItemIndex === expectedIndex;
  
  // Find feedback element
  let feedbackEl = questionDiv.querySelector('.question-feedback');
  if (!feedbackEl) {
    feedbackEl = document.createElement('div');
    feedbackEl.className = 'question-feedback';
    feedbackEl.setAttribute('aria-live', 'polite');
    questionDiv.appendChild(feedbackEl);
  }
  
  if (isCorrect) {
    // Correct selection - mark as chosen and advance step
    sequenceState.chosenIndices.push(selectedItemIndex);
    sequenceState.subStepIndex++;
    
    // Update button state
    btn.disabled = true;
    btn.classList.add('locked', 'correct');
    btn.setAttribute('aria-disabled', 'true');
    
    // Announce success
    TranscriptModule.announceToScreenReader(`${i18nService.getString('correct') || 'Correct!'} ${i18nService.formatUIString('nextStep', { current: sequenceState.subStepIndex, total: question.items.length })}`);
    feedbackEl.textContent = '';
    
    // Log correct step
    logEvent(EVENT_TYPES['SEQUENCE_STEP_CORRECT']!, {
      clipId: clip.id,
      questionIndex: appState.currentQuestionIndex,
      stepIndex: sequenceState.subStepIndex - 1,
      selectedItemIndex: selectedItemIndex,
      subStep: sequenceState.subStepIndex,
      subStepCount: question.items.length
    });
    
    // Check if sequence is complete
    if (sequenceState.subStepIndex >= question.items.length) {
      // Sequence complete!
      logEvent(EVENT_TYPES['SEQUENCE_COMPLETE']!, {
        clipId: clip.id,
        questionIndex: appState.currentQuestionIndex,
        totalSteps: question.items.length
      });
      
      // Store answer as correct
      if (!appState.clipAnswers.has(clip.id)) {
        appState.clipAnswers.set(clip.id, []);
      }
      const answers = appState.clipAnswers.get(clip.id);
      if (answers) {
        answers[appState.currentQuestionIndex] = true;
      }
      
      // Clear sequence state
      appState.sequenceState = undefined;
      
      // Show completion message
      feedbackEl.textContent = i18nService.getString('sequenceComplete') || 'Sequence complete!';
      TranscriptModule.announceToScreenReader(`${i18nService.getString('sequenceComplete') || 'Sequence complete!'} ${i18nService.getString('correct') || 'Well done!'}`);
      
      // Increment question index
      appState.currentQuestionIndex++;
      
      // Add Continue or Finish button
      if (appState.currentQuestionIndex < clip.questions.length) {
        // Not the last question - add Continue button
        const continueBtn = document.createElement('button');
        continueBtn.id = 'nextQuestionInClip';
        continueBtn.textContent = i18nService.getString('continue') || 'Continue';
        continueBtn.className = 'inline-continue-btn';
        
        continueBtn.addEventListener('click', () => {
          renderCurrentQuestion(clip, appState.currentClipIdx);
        });
        
        questionDiv.appendChild(continueBtn);
      } else {
        // Last question - mark clip as completed and show summary inline
        markClipCompleted(clip.id);
        updateProgressUI();
        
        // Create a container for the summary that will be appended below the question
        const summaryContainer = document.createElement('div');
        summaryContainer.className = 'inline-summary-container mt-4';
        questionDiv.appendChild(summaryContainer);
        
        // Render the summary chip into this container
        renderSummaryChip(clip, summaryContainer);
      }
      
      // Update progress bar
      updateQuestionProgressBar();
    } else {
      // More steps to go - re-render to update UI
      setTimeout(() => {
        renderSequenceQuestion(clip, appState.currentClipIdx);
      }, 300);
    }
  } else {
    // Incorrect selection - show feedback but keep question visible
    feedbackEl.textContent = i18nService.getString('tryAgain') || 'Not quite. Try again!';
    TranscriptModule.announceToScreenReader('Incorrect. Please try again.');
    
    // Flash the button red briefly
    const incorrectBtn = buttons[selectedItemIndex];
    if (incorrectBtn) {
      incorrectBtn.classList.add('incorrect');
      setTimeout(() => {
        incorrectBtn.classList.remove('incorrect');
      }, 1000);
    }
    
    // Log incorrect step
    logEvent(EVENT_TYPES['SEQUENCE_STEP_INCORRECT']!, {
      clipId: clip.id,
      questionIndex: appState.currentQuestionIndex,
      stepIndex: sequenceState.subStepIndex,
      selectedItemIndex: selectedItemIndex,
      expectedItemIndex: expectedIndex,
      subStep: sequenceState.subStepIndex + 1,
      subStepCount: question.items.length
    });
  }
}
